/**
 * Step 3: Validation Preview
 *
 * Shows sample data preview with validation before upload.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { Badge } from '../../ui/badge';
import { AlertCircle, CheckCircle2, AlertTriangle, Eye } from 'lucide-react';

const PREVIEW_ROWS = 5;

export default function ValidationPreviewStep({
  csvData,
  fieldMapping,
  onNext,
  onBack,
  onValidationResults
}) {
  const { t } = useTranslation();
  const [previewData, setPreviewData] = useState([]);
  const [validationSummary, setValidationSummary] = useState(null);
  const [showAllColumns, setShowAllColumns] = useState(false);

  useEffect(() => {
    processPreviewData();
  }, [csvData, fieldMapping]);

  const processPreviewData = () => {
    // Get mapped database fields
    const mappedFields = Object.keys(fieldMapping);

    // Transform preview rows
    const preview = csvData.data.slice(0, PREVIEW_ROWS).map((row, idx) => {
      const transformedRow = { rowNumber: idx + 1, original: row, transformed: {} };

      mappedFields.forEach(csvCol => {
        const dbField = fieldMapping[csvCol];
        const value = row[csvCol];
        transformedRow.transformed[dbField] = value;
      });

      return transformedRow;
    });

    setPreviewData(preview);

    // Calculate validation summary
    const summary = {
      totalRows: csvData.rowCount,
      mappedFields: Object.keys(fieldMapping).length,
      readyToUpload: true,
      warnings: [],
    };

    // Add warnings for common issues
    if (csvData.rowCount > 1000) {
      summary.warnings.push({
        type: 'info',
        message: t('bulkUpload.steps.preview.warnings.largeFile', {
          count: csvData.rowCount
        })
      });
    }

    // Check for empty columns
    const firstRow = csvData.data[0];
    Object.entries(fieldMapping).forEach(([csvCol, dbField]) => {
      const hasData = csvData.data.some(row => row[csvCol]?.trim());
      if (!hasData) {
        summary.warnings.push({
          type: 'warning',
          message: t('bulkUpload.steps.preview.warnings.emptyColumn', {
            field: t(`bulkUpload.fields.${dbField}`)
          })
        });
      }
    });

    setValidationSummary(summary);
    onValidationResults(summary);
  };

  const getMappedColumns = () => {
    if (showAllColumns) {
      return Object.keys(fieldMapping);
    }
    // Show only first 6 mapped columns for compact view
    return Object.keys(fieldMapping).slice(0, 6);
  };

  const getDisplayValue = (value) => {
    if (!value || value === '') return '-';
    if (value.length > 50) return value.substring(0, 47) + '...';
    return value;
  };

  const handleNext = () => {
    onNext();
  };

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <h3 className="font-semibold text-blue-900 mb-2">
          {t('bulkUpload.steps.preview.instructions.title')}
        </h3>
        <p className="text-sm text-blue-800">
          {t('bulkUpload.steps.preview.instructions.description')}
        </p>
      </div>

      {/* Validation Summary */}
      {validationSummary && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-blue-900">
                  {validationSummary.totalRows}
                </div>
                <div className="text-sm text-blue-700">
                  {t('bulkUpload.steps.preview.summary.totalStudents')}
                </div>
              </div>
              <Eye className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-green-900">
                  {validationSummary.mappedFields}
                </div>
                <div className="text-sm text-green-700">
                  {t('bulkUpload.steps.preview.summary.mappedFields')}
                </div>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className={`p-4 rounded-lg ${
            validationSummary.readyToUpload ? 'bg-green-50' : 'bg-yellow-50'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">
                  {validationSummary.readyToUpload ? '✓' : '!'}
                </div>
                <div className="text-sm">
                  {validationSummary.readyToUpload
                    ? t('bulkUpload.steps.preview.summary.ready')
                    : t('bulkUpload.steps.preview.summary.hasIssues')
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Warnings */}
      {validationSummary?.warnings.length > 0 && (
        <div className="space-y-2">
          {validationSummary.warnings.map((warning, idx) => (
            <Alert
              key={idx}
              variant={warning.type === 'warning' ? 'default' : 'default'}
              className={
                warning.type === 'warning'
                  ? 'border-yellow-300 bg-yellow-50'
                  : 'border-blue-300 bg-blue-50'
              }
            >
              {warning.type === 'warning' ? (
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-blue-600" />
              )}
              <AlertDescription className={
                warning.type === 'warning' ? 'text-yellow-800' : 'text-blue-800'
              }>
                {warning.message}
              </AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      {/* Preview Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-center">
          <div>
            <h3 className="font-semibold text-gray-900">
              {t('bulkUpload.steps.preview.table.title')}
            </h3>
            <p className="text-xs text-gray-600">
              {t('bulkUpload.steps.preview.table.showing', {
                count: PREVIEW_ROWS,
                total: csvData.rowCount
              })}
            </p>
          </div>

          {Object.keys(fieldMapping).length > 6 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAllColumns(!showAllColumns)}
            >
              {showAllColumns
                ? t('bulkUpload.steps.preview.table.showLess')
                : t('bulkUpload.steps.preview.table.showAll', {
                    count: Object.keys(fieldMapping).length - 6
                  })
              }
            </Button>
          )}
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">#</TableHead>
                {getMappedColumns().map((csvCol, idx) => {
                  const dbField = fieldMapping[csvCol];
                  return (
                    <TableHead key={idx} className="min-w-[150px]">
                      <div className="space-y-1">
                        <div className="font-semibold">
                          {t(`bulkUpload.fields.${dbField}`)}
                        </div>
                        <div className="text-xs text-gray-500 font-normal">
                          {csvCol}
                        </div>
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {previewData.map((row) => (
                <TableRow key={row.rowNumber}>
                  <TableCell className="font-medium text-gray-500">
                    {row.rowNumber}
                  </TableCell>
                  {getMappedColumns().map((csvCol, idx) => {
                    const dbField = fieldMapping[csvCol];
                    const value = row.original[csvCol];
                    return (
                      <TableCell key={idx}>
                        {getDisplayValue(value)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Important Notice */}
      <Alert className="border-blue-300 bg-blue-50">
        <AlertCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <div className="font-semibold mb-1">
            {t('bulkUpload.steps.preview.notice.title')}
          </div>
          <p className="text-sm">
            {t('bulkUpload.steps.preview.notice.description')}
          </p>
        </AlertDescription>
      </Alert>

      {/* Expected Processing Time */}
      {validationSummary && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-gray-900">
                {t('bulkUpload.steps.preview.processing.title')}
              </div>
              <div className="text-sm text-gray-600">
                {t('bulkUpload.steps.preview.processing.estimate', {
                  seconds: Math.ceil(validationSummary.totalRows / 20)
                })}
              </div>
            </div>
            <Badge variant="outline" className="text-sm">
              ~{Math.ceil(validationSummary.totalRows / 20)}s
            </Badge>
          </div>
        </div>
      )}
    </div>
  );
}
